import { Chip, Grid } from "@mui/material";
import { ReactComponent as MailIcon } from "assets/mail_icon.svg";
import { editPreferredEmail, editRoleSelection } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import DialogFormation from "components/common/DialogFormation";
import Input from "components/common/Input";
import ButtonLink from "components/common/Link";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Typography from "components/common/Typography";
import Dropdown from "components/common/dropdowns/DropDown";
import MultipleSelectCheckmarks from "components/common/dropdowns/MultiSelect";
import { isEmpty } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import colors from "styles/Colors";
import { tokens } from "styles/Tokens";
import { accessTypes, emailRegex, uiToPayloadExperienceLevel } from "utils/CommonConstants";
import { converter_ArrayString, getLoggedInUserDetails, getUserHltId } from "utils/CommonFunctions";
import { getUserName } from "utils/StoreUtils";
import { responseTypes } from "utils/Types";
import MyPermissions from "../AdminConsole/MyPermission/MyPermissions";
import Mcqlist from "../Onboarding/Mcqlist";
import { setOpenEmailDialog } from "./Dashboard.slice";
import { setUserExperienceLevel } from "./HotelUserDashboard.slice";
import { setUseEffectState } from "./PropertyOverview/PropertyOverview.slice";
import "./index.scss";

const PreferredEmailDialog = () => {
  const dispatch = useAppDispatch();
  const isDialogOpen: any = useAppSelector((state) => state?.Dashboard.shouldShowEmailDialog);
  const loggedInUserDetails = getLoggedInUserDetails();
  const filters = useAppSelector((state) => state?.app?.userRoles?.filters);
  const roles = useAppSelector((state) => state?.app?.userRoles?.roles);
  const [selectedList, setSelectedList] = useState<any[]>([]);
  const associateMetadata = useAppSelector((state) => state.hotelUserDashboardSlice.associateMetadata);
  const [experienceLevel, setExperienceLevel] = useState<string>(associateMetadata?.experienceLevel);
  const [checkedList, setCheckedList] = useState(roles);
  const { isMobileScreen } = useContext(ThemeContext);
  const isCorp = useAppSelector((state) => state.app.loggedInUserDetails.isCorp);
  const isAbovePropertyUser = useAppSelector((state) => state.app.loggedInUserDetails.isAbovePropertyUser);
  const impersonationDetails = useAppSelector((state) => state.app.impersonationDetails);
  const isImpersonated = impersonationDetails?.isImpersonated;
  const onboardingCompletionDatePresent = useAppSelector((state) => state.Onboarding.onboardingCompletionDatePresent);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm();

  const handleClose = () => {
    setCheckedList(roles);
    dispatch(setOpenEmailDialog(false));
  };

  const onSubmit = async (data: any) => {
    let roleResponse: any = {};
    if (!isCorp && !isAbovePropertyUser) {
      const props = {
        hltId: getUserHltId(),
        roles: selectedList
      };
      roleResponse = await dispatch(editRoleSelection(props));
    }
    const response: any = await dispatch(editPreferredEmail({ preferredEmail: data.listName, experienceLevel }));
    if (experienceLevel !== associateMetadata?.experienceLevel) dispatch(setUserExperienceLevel(experienceLevel));
    if (Object.keys(roleResponse).length > 0 && roleResponse.payload.status !== responseTypes.success) return;
    if (response.payload.status === responseTypes.success) {
      dispatch(
        setFeedbackDetails({
          shouldShow: true,
          setState: setFeedbackDetails({ shouldShow: false }),
          message: "Account details have been updated",
          variant: "success"
        })
      );
      handleClose();
      if (experienceLevel !== associateMetadata?.experienceLevel) {
        window.location.reload();
        return;
      }
      dispatch(setUseEffectState(true));
    }
  };

  const getSelectedList: any = (list: any, setList: any) => {
    return list?.map((el: any) => {
      return (
        <>
          <Chip
            key={el}
            className="role-chip"
            sx={{
              "&": { borderRadius: "50px", marginRight: "4px", marginBottom: "4px" },
              "& .MuiChip-deleteIcon": {
                color: `${tokens.Text.General.Color}`,
                marginRight: "4px",
                marginBottom: "4px"
              }
            }}
            label={el}
            onDelete={() => {
              let tempList = list?.filter((val: any) => el !== val);
              setList(tempList);
            }}
          />
        </>
      );
    });
  };
  const handleChange = (event: any) => {
    let { value }: any = event.target;

    let updatedList = [...selectedList];
    updatedList.push(value);
    setSelectedList(updatedList);
  };
  const filterList: any = (value: any) => {
    if (!isEmpty(value)) {
      let tempList: any = roles?.filter((option: any) => value.includes(option?.filterCategory));
      setCheckedList(tempList);
    } else {
      setCheckedList(roles);
    }
  };
  useEffect(() => {
    setCheckedList(roles);
  }, [roles]);

  useEffect(() => {
    setSelectedList(loggedInUserDetails?.roles);
    setValue("listName", loggedInUserDetails?.preferredEmail || loggedInUserDetails?.email);
  }, [loggedInUserDetails, isImpersonated]);

  const handleSave = () => {
    if (isImpersonated && !isAbovePropertyUser && !onboardingCompletionDatePresent) {
      dispatch(
        setFeedbackDetails({
          variant: "warning",
          shouldShow: true,
          setState: setFeedbackDetails({ shouldShow: false }),
          message: `'${getUserName()}' has never logged into BOOST. No data will display. `
        })
      );
    } else {
      handleSubmit(onSubmit)();
    }
  };

  const modifyParam = ({ cmsPlanId, planName, innCode, ...rest }: any) => rest;

  return (
    <div>
      {isDialogOpen && (
        <DialogFormation
          openDialog={isDialogOpen}
          parentOverflow={true}
          contentClass="prefered-email"
          handleClose={() => {
            setValue(
              "listName",
              loggedInUserDetails?.preferredEmail ? loggedInUserDetails?.preferredEmail : loggedInUserDetails?.email
            );
            setSelectedList(loggedInUserDetails.roles);
            handleClose();
          }}
          title="Account Settings"
          modifyParam={modifyParam}
          actionConfig={
            <div className="modal-config-wrapper">
              <Button
                label="Cancel"
                onClick={() => {
                  setValue(
                    "listName",
                    loggedInUserDetails?.preferredEmail
                      ? loggedInUserDetails?.preferredEmail
                      : loggedInUserDetails?.email
                  );
                  setSelectedList(loggedInUserDetails.roles);
                  handleClose();
                }}
                variant="secondary"
              />
              <Button
                disabled={!selectedList?.length && !isAbovePropertyUser && !isCorp}
                accessType={isImpersonated ? accessTypes.delete : ""}
                label="Save"
                onClick={handleSave}
                variant="primary"
              />
            </div>
          }
        >
          <div className="account-setting-modal" style={{ position: "relative" }}>
            {!isCorp && !isAbovePropertyUser ? (
              <div className="modal-wrapper">
                <div className="content-wrapper">
                  <div className="left-content">
                    <Typography variant={"h5"} showBold color={tokens.Text.General.PrimaryHeadline}>
                      Role
                    </Typography>
                    <Typography variant="body2" color={colors.Neutral.Neutral400} className="role-desc">
                      Update your current role(s) to receive suggested plans that are relevant to you
                    </Typography>
                    <Typography
                      variant={"body2"}
                      showBold
                      color={tokens.Text.General.Secondary}
                      className="current-role"
                    >
                      Current role(s)
                    </Typography>
                    <div className="selected-tags"> {getSelectedList(selectedList, setSelectedList)}</div>
                  </div>
                  <div className="right-content">
                    <div className="filter-actions">
                      <Typography>Available roles</Typography>
                      <div className="vertical-center">
                        <Typography variant="body2" color={tokens.Text.General.Secondary}>
                          Filter by
                        </Typography>
                        <MultipleSelectCheckmarks
                          minWidth="104px"
                          name="roleType"
                          label="Role type"
                          options={converter_ArrayString(filters, "name")}
                          onChange={(value: any) => filterList(value)}
                        />
                      </div>
                    </div>
                    <div>
                      <Mcqlist
                        sx={{ backgroundColor: colors.Neutral.Neutral0 }}
                        list={checkedList}
                        handleChange={handleChange}
                        selectedList={selectedList}
                        role={true}
                        filterData={true}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-wrapper">
                  <div className="form-left">
                    <Typography variant="body2">Experience Level</Typography>
                  </div>
                  <div className="form-right">
                    <Dropdown
                      name="experienceLevel"
                      handleChangeUpdate={(val) =>
                        setExperienceLevel(
                          uiToPayloadExperienceLevel?.filter((item: any) => item?.label === val)?.[0]?.value || ""
                        )
                      }
                      initialValue={
                        uiToPayloadExperienceLevel?.filter(
                          (item: any) => item?.value === associateMetadata?.experienceLevel
                        )?.[0]?.label || ""
                      }
                      options={converter_ArrayString(uiToPayloadExperienceLevel?.map((option) => option.label))}
                      sx={{ margin: "4px 0px", "& .MuiSelect-select": { marginLeft: "11.43px" } }}
                    />
                  </div>
                </div>
                <form>
                  <div className="form-wrapper">
                    <div className="form-left">
                      <Typography variant="body2">Preferred email</Typography>
                      <Typography variant={"body2"} color={colors.Neutral.Neutral400}>
                        Change the email where you’ll be receiving all BOOST communications or{" "}
                        <ButtonLink
                          text="Reset to Hilton email"
                          textVariant={"body2"}
                          underline="none"
                          type="button"
                          onClick={(e: any) => {
                            setValue("listName", loggedInUserDetails?.email);
                          }}
                        />
                      </Typography>
                    </div>
                    <div className="form-right">
                      <div style={{ width: "320px" }}>
                        <Input
                          name="listName"
                          register={register}
                          error={errors?.listName}
                          placeholder="New email"
                          value={
                            loggedInUserDetails?.preferredEmail
                              ? loggedInUserDetails?.preferredEmail
                              : loggedInUserDetails?.email
                          }
                          startAdornment={<MailIcon />}
                          validations={{
                            required: true,
                            validate: (v: string) => emailRegex.test(v) || "Email address must be a valid email"
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            ) : (
              <>
                {isCorp && <MyPermissions />}
                <form onSubmit={handleSubmit(onSubmit)} className="fixed-form">
                  <Grid
                    container
                    sx={{ paddingRight: "20px" }}
                    display="flex"
                    flexDirection={isMobileScreen ? "column" : "row"}
                    justifyContent={"space-between"}
                  >
                    {" "}
                    <Grid item xs={12} sm={4.5} spacing={1} marginTop={isMobileScreen ? "8px" : ""}>
                      <Typography variant="body2" showBold color={tokens.Text.General.PrimaryHeadline}>
                        Preferred Email
                      </Typography>
                      <Typography variant={"body2"} color={colors.Neutral.Neutral400}>
                        Change the email where you’ll be receiving all BOOST communications or
                      </Typography>
                      <ButtonLink
                        text="Reset to Hilton email"
                        textVariant={"body2"}
                        underline="none"
                        type="button"
                        onClick={(e: any) => {
                          setValue("listName", loggedInUserDetails?.email);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={7} marginTop="24px">
                      {" "}
                      <Input
                        // label="Change contact email"
                        name="listName"
                        register={register}
                        error={errors?.listName}
                        placeholder="New email"
                        value={
                          loggedInUserDetails?.preferredEmail
                            ? loggedInUserDetails?.preferredEmail
                            : loggedInUserDetails?.email
                        }
                        startAdornment={<MailIcon />}
                        validations={{
                          required: true,
                          validate: (v: string) => emailRegex.test(v) || "Email address must be a valid email"
                        }}
                      />
                    </Grid>
                  </Grid>
                  {errors?.importFile && (
                    <Typography variant="label" color={tokens.Text.Feedback.Alert}>
                      {errors?.importFile?.message ?? "This field is required to be completed. "}
                    </Typography>
                  )}
                </form>
              </>
            )}
          </div>
        </DialogFormation>
      )}
    </div>
  );
};

export default PreferredEmailDialog;
